import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Phone, Clock, Shield, Plane, Users, CheckCircle2, Car } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import ServiceFAQ from "@/components/ServiceFAQ";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";

const AirportTransferSAT = () => {
  const features = [
    {
      icon: <Plane className="w-6 h-6" />,
      title: "Always on time with live flight tracking",
      description: "We monitor your flight in real-time to adjust pickup times for delays or early arrivals."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Meet-and-greet available at baggage claim",
      description: "Our professional chauffeur will meet you at baggage claim with a personalized name sign."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Professional, vetted chauffeurs",
      description: "Experienced, background-checked drivers dedicated to your safety and comfort."
    },
    {
      icon: <Car className="w-6 h-6" />,
      title: "Clean late-model sedans and SUVs",
      description: "Immaculate, luxury vehicles maintained to the highest standards."
    },
    {
      icon: <CheckCircle2 className="w-6 h-6" />,
      title: "Fixed, transparent pricing",
      description: "No surge pricing, no surprises. Get an instant quote upfront."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Child seats available on request",
      description: "Family-friendly service with complimentary child safety seats."
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "24/7 service and support",
      description: "Round-the-clock availability for early morning departures and late-night arrivals."
    }
  ];

  const fleet = [
    {
      name: "Luxury SUV",
      capacity: "Up to 6 passengers, 6–8 bags",
      description: "Comfortable executive transportation for individuals, couples, families, and small groups"
    }
  ];

  const faqs = [
    {
      question: "Do you track flights at SAT?",
      answer: "Yes. We track your flight in real time and adjust pickup if you're early or delayed."
    },
    {
      question: "Where will I meet my driver?",
      answer: "Choose curbside pickup or meet-and-greet inside baggage claim with a name sign."
    },
    {
      question: "What if my flight is delayed?",
      answer: "We automatically adjust and include a grace period. Extended delays are accommodated—just keep us updated if your flight number changes."
    },
    {
      question: "Can you provide child seats?",
      answer: "Yes—please request during booking so we can reserve the right seat(s)."
    },
    {
      question: "What areas do you serve?",
      answer: "All of Greater San Antonio, including downtown, Stone Oak, Alamo Heights, Boerne, New Braunfels, and more."
    },
    {
      question: "Do you offer early morning or late-night pickups?",
      answer: "Yes—our service is available 24/7 with advance reservation."
    }
  ];

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://ricksblackcarservicesantonio.com/sat-airport-transfers#service",
    "name": "SAT Airport Transfers",
    "serviceType": "Airport Transportation",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://ricksblackcarservicesantonio.com/#business"
    },
    "areaServed": {
      "@type": "City",
      "name": "San Antonio"
    },
    "description": "Private airport car service to and from San Antonio International Airport (SAT) with professional chauffeurs, flight tracking, and 24/7 availability.",
    "image": [
      "https://ricksblackcarservicesantonio.com/og-image.jpg"
    ],
    "offers": {
      "@type": "Offer",
      "url": "https://ricksblackcarservicesantonio.com/sat-airport-transfers",
      "priceCurrency": "USD",
      "price": "85.00",
      "priceValidUntil": "2025-12-31",
      "availability": "https://schema.org/InStock"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const imageSchema = {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    "url": "https://ricksblackcarservicesantonio.com/assets/sat-airport-entrance.jpeg",
    "caption": "San Antonio International Airport (SAT) terminal entrance with luxury black car service pickup",
    "description": "Rick's Black Car Service provides professional airport transfers at San Antonio International Airport (SAT) with meet-and-greet service and luxury transportation",
    "width": 1920,
    "height": 1080,
    "publisher": {
      "@type": "Organization",
      "name": "Rick's Black Car Service",
      "url": "https://ricksblackcarservicesantonio.com/"
    },
    "copyrightNotice": "© Rick's Black Car Service",
    "creditText": "Rick's Black Car Service"
  };

  return (
    <>
      <SEO
        title="SAT Airport Transfers | San Antonio Black Car | Rick's"
        description="Professional airport car service to and from SAT. Flight tracking, meet‑and‑greet, 24/7 pickups, luxury SUV. Upfront rates. Book online or call."
        canonicalPath="/sat-airport-transfers"
        schema={[serviceSchema, faqSchema, imageSchema]}
      />
      
      <div className="min-h-screen">
        <Navbar />
        <main className="pt-20">
          {/* Hero Section */}
          <section className="relative py-20 luxury-gradient">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center space-y-6">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground">
                  SAT Airport Transfers – San Antonio Black Car Service
                </h1>
                <p className="text-xl text-muted-foreground">
                  Arrive relaxed and on time with Rick's Black Car Service. We provide private, door‑to‑door airport transfers to and from San Antonio International Airport (SAT) with professional chauffeurs, flight tracking, and 24/7 availability.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                  <Button className="btn-luxury text-lg px-8 py-6" asChild>
                    <a href="http://ricksblackcarservicesantonio.square.site" target="_blank" rel="noopener noreferrer">
                      Book 24/7
                    </a>
                  </Button>
                  <Button variant="outline" className="text-lg px-8 py-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground" asChild>
                    <a href="tel:+12102967409">
                      <Phone className="w-5 h-5 mr-2" />
                      Call (210) 296-7409
                    </a>
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground pt-2">
                  Instant confirmations. Text updates when your driver is en route.
                </p>
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                  Why Ride with Us
                </h2>
                <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                  Every detail is handled so you can focus on your journey.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {features.map((feature, index) => (
                  <Card key={index} className="card-luxury">
                    <CardContent className="p-6 space-y-3">
                      <div className="text-primary">{feature.icon}</div>
                      <h3 className="text-lg font-semibold text-foreground">{feature.title}</h3>
                      <p className="text-muted-foreground text-sm">{feature.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* How SAT Pickup Works Section */}
          <section className="py-20 bg-secondary">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto space-y-8">
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground text-center mb-8">
                  How SAT Pickup Works
                </h2>

                <div className="space-y-6 text-muted-foreground">
                  <div className="flex gap-4">
                    <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <p>We monitor your flight and adjust pickup time automatically.</p>
                  </div>
                  <div className="flex gap-4">
                    <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <p>After landing, your driver texts exact meeting point details.</p>
                  </div>
                  <div className="flex gap-4">
                    <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <p>Curbside or meet‑and‑greet options available.</p>
                  </div>
                  <div className="flex gap-4">
                    <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <p>Need extra time for bags? No problem—complimentary grace period included.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Fleet Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground text-center mb-12">
                  Fleet
                </h2>
                <div className="max-w-xl mx-auto">
                  {fleet.map((vehicle, index) => (
                    <Card key={index} className="card-luxury">
                      <CardContent className="p-8 space-y-4 text-center">
                        <Car className="w-12 h-12 text-primary mx-auto" />
                        <h3 className="text-2xl font-semibold text-foreground">{vehicle.name}</h3>
                        <p className="text-lg text-muted-foreground font-medium">{vehicle.capacity}</p>
                        <p className="text-muted-foreground">{vehicle.description}</p>
                        <ul className="text-left space-y-2 text-sm text-muted-foreground mt-4">
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                            <span>Executive comfort with rear climate control</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                            <span>Complimentary bottled water</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                            <span>Phone chargers and USB ports</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                            <span>Child seats available on request</span>
                          </li>
                        </ul>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <p className="text-center text-sm text-muted-foreground mt-8">
                  For groups larger than 6 passengers, we can schedule multiple SUV trips to accommodate your party.
                </p>
              </div>
            </div>
          </section>

          {/* Service Area Section */}
          <section className="py-20 bg-secondary">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center space-y-6">
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground">
                  Service Area
                </h2>
                <p className="text-muted-foreground text-lg">
                  SAT to Downtown, River Walk, Alamo Heights, Stone Oak, The Rim/La Cantera, Shavano Park, Boerne, New Braunfels, Helotes, and more across Greater San Antonio.
                </p>
              </div>
            </div>
          </section>

          {/* Transparent Rates Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center space-y-6">
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground">
                  Transparent Rates
                </h2>
                <p className="text-muted-foreground text-lg">
                  Get an instant quote for your route. No surge pricing, no surprises. For hourly or multi‑stop itineraries, request a custom quote.
                </p>
              </div>
            </div>
          </section>

          {/* Related Services Section */}
          <section className="py-20 bg-secondary">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground text-center mb-12">
                  Related Services
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card className="card-luxury">
                    <CardContent className="p-6 text-center space-y-3">
                      <h3 className="text-lg font-semibold text-foreground">Corporate Travel</h3>
                      <p className="text-sm text-muted-foreground">Hourly/as‑directed executive service</p>
                      <Button variant="outline" asChild className="w-full">
                        <Link to="/services/corporate-travel">View Corporate Travel</Link>
                      </Button>
                    </CardContent>
                  </Card>
                  <Card className="card-luxury">
                    <CardContent className="p-6 text-center space-y-3">
                      <h3 className="text-lg font-semibold text-foreground">Wedding Transportation</h3>
                      <p className="text-sm text-muted-foreground">Elegant transportation for your special day</p>
                      <Button variant="outline" asChild className="w-full">
                        <Link to="/services/wedding-transportation">View Wedding Services</Link>
                      </Button>
                    </CardContent>
                  </Card>
                  <Card className="card-luxury">
                    <CardContent className="p-6 text-center space-y-3">
                      <h3 className="text-lg font-semibold text-foreground">Hill Country Wine Tours</h3>
                      <p className="text-sm text-muted-foreground">Luxury wine country experiences</p>
                      <Button variant="outline" asChild className="w-full">
                        <Link to="/services/wine-tours">View Wine Tours</Link>
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </section>

          {/* Trust Note Section */}
          <section className="py-12">
            <div className="container mx-auto px-4">
              <div className="max-w-3xl mx-auto text-center">
                <p className="text-muted-foreground text-lg italic">
                  Rated 5‑star by clients for punctuality, comfort, and service. Your itinerary is our priority.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-20 bg-secondary">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <ServiceFAQ faqs={faqs} />
              </div>
            </div>
          </section>

          {/* Bottom CTA Section */}
          <section className="py-20 luxury-gradient">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center space-y-8">
                <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground">
                  Ready to Book Your SAT Airport Transfer?
                </h2>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button className="btn-luxury text-lg px-8 py-6" asChild>
                    <a href="http://ricksblackcarservicesantonio.square.site" target="_blank" rel="noopener noreferrer">
                      Book 24/7
                    </a>
                  </Button>
                  <Button variant="outline" className="text-lg px-8 py-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground" asChild>
                    <a href="tel:+12102967409">
                      <Phone className="w-5 h-5 mr-2" />
                      Call (210) 296-7409
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Mobile Sticky Bar */}
          <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-t border-border/50 shadow-lg">
            <div className="container mx-auto px-4 py-3">
              <div className="flex items-center justify-center gap-3">
                <Button className="btn-luxury flex-1 max-w-[200px]" asChild>
                  <a href="tel:+12102967409">
                    <Phone className="w-5 h-5 mr-2" />
                    Call
                  </a>
                </Button>
                <Button className="btn-luxury flex-1 max-w-[200px]" asChild>
                  <a href="http://ricksblackcarservicesantonio.square.site" target="_blank" rel="noopener noreferrer">
                    Book Now
                  </a>
                </Button>
              </div>
            </div>
          </div>

          {/* Instant Quote CTA */}
          <InstantQuoteCTA />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default AirportTransferSAT;
